/**
 * SiteAccessGuard — Faz 52
 *
 * Site-scoped route'larda (örn. /sozlesmeler/:siteId, /irsaliye-arsivi/:siteId)
 * kullanıcının o şantiyede ilgili modüle erişim yetkisi olup olmadığını kontrol
 * eder. Yetkisi yoksa "Bu şantiyede yetkiniz bulunmuyor" bloke ekranı gösterir.
 *
 * Admin (role === "yonetici") → bypass.
 * Kullanıcının site_scope_by_module[slug] alanı "all" veya site_id içeriyorsa izin verir.
 */
import React from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getSlugForPath } from "@/lib/moduleMenus";
import { ShieldWarning, ArrowLeft, House } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

// Bu route'larda URL'deki 24-hex ID bir KAYIT ID'sidir (şantiye değil) — guard uygulanmaz.
const RECORD_ID_ROUTES = [
  /^\/sozlesmeler\/detay\//,
  /^\/teklif-talepleri\/(?!santiye\/)[^/]+$/,
  /^\/teklifler\/[^/]+$/,
  /^\/masraf\/duzenle\//,
  /^\/masraf\/talep\//,
];

/** URL path'inden siteId benzeri segmenti çıkarır (24-hex Mongo ObjectId). */
function extractSiteIdFromPath(pathname) {
  if (!pathname || RECORD_ID_ROUTES.some((re) => re.test(pathname))) return null;
  const parts = pathname.split("/").filter(Boolean);
  for (const p of parts) {
    if (OBJECT_ID_RE.test(p)) return p;
  }
  return null;
}

export default function SiteAccessGuard({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const params = useParams();
  const nav = useNavigate();

  if (!user) return children;
  // Admin bypass
  if ((user.role || "").toLowerCase() === "yonetici") return children;

  // Hangi modüle ait?
  const slug = getSlugForPath(loc.pathname);
  if (!slug) return children;

  // URL'den siteId'yi çek
  const siteId = params.siteId || extractSiteIdFromPath(loc.pathname);
  if (!siteId) return children; // şantiye seçimli landing — guard'a tabi değil

  const scopeMap = user.site_scope_by_module || {};
  const allowed = scopeMap[slug] || [];
  const hasAll = allowed.includes("all");
  const hasSite = hasAll || allowed.includes(siteId);
  if (hasSite) return children;

  // Yetki yok ekranı
  return (
    <div
      className="min-h-[70vh] flex items-center justify-center px-4 py-10"
      data-testid="site-access-denied"
    >
      <div className="max-w-xl w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-10 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mb-5">
          <ShieldWarning size={30} weight="duotone" className="text-rose-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Bu şantiyede yetkiniz bulunmuyor
        </h1>
        <p className="text-slate-600 mb-6">
          Seçtiğiniz şantiyede bu modül için erişim yetkiniz atanmamış. Lütfen
          sistem yöneticinize danışın veya başka bir şantiye seçin.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            onClick={() => nav(-1)}
            data-testid="site-denied-back-btn"
          >
            <ArrowLeft size={16} className="mr-1.5" />
            Şantiye Seçimine Dön
          </Button>
          <Button
            onClick={() => nav("/", { replace: true })}
            className="bg-[#1B4776] hover:bg-[#163a63] text-white"
            data-testid="site-denied-home-btn"
          >
            <House size={16} className="mr-1.5" />
            Ana Sayfa
          </Button>
        </div>
      </div>
    </div>
  );
}
